import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { getPurposes } from '../../../../services/Core/PurposeService';
import { getObjectives } from '../../../../services/Core/ObjectivesServices';
import QuestionCard from './QuestionCard';
import { Button } from '../../../../components/forms/Buttons/Button';
import { postSaveQuestionAsDraft } from '../../../../services/MyQuestions/QuestionService';
import { draftFailureMessage, failedDrafts, savedDraftIndexes } from '../../../../utils/draftSave';
import { v4 as uuidv4 } from 'uuid';
import questionReducer, {
  cardHandlers,
  getQuestionWarnings,
  getSaveErrorMessage,
  scrollToFirstInvalid,
  validateQuestions,
} from './QuestionUtils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import InfoCard from '../../../../components/Cards/InfoCard';
import { isSubjectFolder } from '../../../../utils/folders';

const focusRing =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600';

// A new question with options A and B (their own uuids: errors and drag and drop go by them).
const blankQuestion = (id) => ({
  id,
  body: '',
  subtopic: '',
  subtopic_name: '',
  purpose: '',
  objective: '',
  answer_keys: [
    { uuid: uuidv4(), option: 'A', body: 'A', is_correct: false },
    { uuid: uuidv4(), option: 'B', body: 'B', is_correct: false },
  ],
});

// `folder`: the exam's subject the questions are created in, where they need a subtopic and a difficulty too
// (to go on to the final view; a draft may lack them). Back from the final view, the step starts from the
// questions it had (`data.questions`) and the errors the save found there (`initialErrors`, by question id).
export default function Questions({ next, back, data, handleData, passage, folder = null, initialErrors = null }) {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const [questions, dispatch] = useReducer(questionReducer, null, () =>
    data?.questions?.length ? data.questions : [blankQuestion(0)],
  );
  const [purposes, setPurposes] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [errors, setErrors] = useState(() => initialErrors || {});
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const inSubject = isSubjectFolder(folder);

  // Leaving the step (Back, or a passed step in the header) keeps what was written: it opens with it again.
  const written = useRef(questions);
  written.current = questions;
  useEffect(() => {
    const kept = written;
    return () => handleData({ questions: kept.current });
  }, [handleData]);

  const getDatas = useCallback(
    (signal) => {
      const promises = [
        getPurposes({ signal }),
        getObjectives({ signal, params: {} }),
      ];
      setLoading(true);
      Promise.all(promises)
        .then(([purposesRes, objectivesRes]) => {
          const pArr = Array.isArray(purposesRes) ? purposesRes : purposesRes.results || [];
          const oArr = Array.isArray(objectivesRes) ? objectivesRes : objectivesRes.results || [];
          setPurposes(pArr);
          setObjectives(oArr);
        })
        .catch(() => {})
        .finally(() => {
          if (!signal?.aborted) {
            setLoading(false);
          }
        });
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    getDatas(controller.signal);
    return () => {
      controller.abort();
    };
  }, [getDatas]);

  // Sent back from the final view with what the save found missing: show the first marked field.
  useEffect(() => {
    if (initialErrors && Object.keys(initialErrors).length) scrollToFirstInvalid();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Marks the errors (by question index) on their cards; false when there are none.
  const throwError = (errors) => {
    const errors_codes = Object.keys(errors);
    // Kept by question id, not position, so they stay with their card when a question above is removed.
    setErrors(Object.fromEntries(errors_codes.map((i) => [questions[i].id, errors[i]])));
    if (errors_codes.length > 0) {
      toast.error(
        t('my_questions.validation.error_on_questions', {
          numbers: errors_codes.map((i) => +i + 1).join(', '),
        }),
      );
      scrollToFirstInvalid();
      return true;
    }
    return false;
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
      <InfoCard
        textComponent={
          <div className="flex flex-row items-start gap-3">
            <InformationCircleIcon className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
            <p className="max-w-3xl text-sm leading-relaxed text-slate-600">
              <span className="font-semibold text-slate-900">{t('my_questions.katex_help.title')}</span>
              <br />
              <Trans
                t={t}
                i18nKey="my_questions.katex_help.body"
                components={{
                  // Trans puts the translated link text in place of "KaTeX".
                  link: (
                    <a
                      href="https://katex.org/docs/supported.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 underline-offset-2 hover:text-blue-700 hover:underline"
                    >
                      KaTeX
                    </a>
                  ),
                }}
              />
              <br />
              {t('my_questions.katex_help.wrap')}
            </p>
          </div>
        }
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />
      {questions.map((q) => {
        // Keyed by id, not position: QuestionCard keeps its own state (body, pickers, editor HTML),
        // so after a removal every card must stay with its own question.
        return (
          <div className="relative group" key={q.id}>
            {questions.length > 1 && (
              <button
                type="button"
                aria-label={t('my_questions.editor.remove_question')}
                title={t('my_questions.editor.remove_question')}
                className={`absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition duration-200 hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 max-md:h-11 max-md:w-11 sm:opacity-0 sm:group-hover:opacity-100 ${focusRing}`}
                onClick={() => {
                  dispatch({ type: 'REMOVE_QUESTION', id: q.id });
                  // A question added later can get this id again and must not inherit these errors.
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next[q.id];
                    return next;
                  });
                }}
              >
                <TrashIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            )}

            <QuestionCard
              loading={loading}
              question={q}
              errors={errors[q.id]}
              required={inSubject}
              purposes={purposes}
              grade={data.grade}
              subject={data.subject}
              objectives={objectives}
              {...cardHandlers(q.id, q, dispatch, setErrors)}
            />
          </div>
        );
      })}
      {/* A side action: a text link under the last question, not a box of its own. */}
      <button
        type="button"
        className={`-mt-2 inline-flex min-h-[2.75rem] w-fit items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 md:min-h-[2.25rem] ${focusRing}`}
        onClick={() =>
          dispatch({
            type: 'ADD_QUESTION',
            newQuestion: blankQuestion(questions[questions.length - 1].id + 1),
          })
        }
      >
        <PlusIcon className="h-4 w-4" aria-hidden="true" /> {t('my_questions.editor.add_question')}
      </button>
      {/* Phones stack the two (44px), the main one on top; wider screens keep them in a row on the right. */}
      <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pb-6 pt-6 sm:flex-row sm:justify-end">
        {back && (
          <Button
            className="max-sm:h-11 sm:mr-auto"
            text={t('buttons.back')}
            Icon={ArrowLeftIcon}
            color="secondary-outline"
            disabled={isSavingDraft}
            onClick={back}
          />
        )}
        <Button
          className="max-sm:h-11"
          text={t('my_questions.actions.save_draft_and_exit')}
          color="secondary-outline"
          loading={isSavingDraft}
          disabled={isSavingDraft}
          onClick={(e) => {
            if (e.detail > 1) return;
            // A draft may lack what review needs: only the basic checks.
            if (throwError(validateQuestions(questions))) return; // IF ERRORS, stop here
            const warns = getQuestionWarnings(questions);
            warns.forEach((w) => toast.warn(w));
            // Same as Final.js: the step-1 subject (and the passage being written for) travel in the draft and
            // are sent when the draft is submitted.
            // A draft written in an exam's subject is kept there (as closed as its questions).
            const draft_questions = questions.map((q) => ({
              draft: { ...q, subject_uuid: data.subject, ...(passage ? { passage_uuid: passage.uuid } : {}) },
              ...(folder ? { folder: folder.uuid } : {}),
            }));

            setIsSavingDraft(true);
            postSaveQuestionAsDraft(draft_questions)
              .then((results) => {
                const failed = failedDrafts(results);
                if (!failed.length) {
                  toast.success(t('my_questions.toasts.saved_as_draft'));
                  navigate('/my-questions');
                  return;
                }
                // Refused drafts stay on the page; the saved ones leave the form, so a retry does not save them twice.
                const saved = savedDraftIndexes(results);
                questions.forEach((q, i) => saved.has(i) && dispatch({ type: 'REMOVE_QUESTION', id: q.id }));
                const message = draftFailureMessage(t, failed, questions.length);
                toast.error(saved.size ? `${message} ${t('my_questions.toasts.drafts_saved_rest')}` : message);
              })
              .catch((e) => {
                toast.error(getSaveErrorMessage(t, e));
              })
              .finally(() => {
                setIsSavingDraft(false);
              });
          }}
        />
        <Button
          className="max-sm:h-11"
          text={t('buttons.next')}
          color="primary"
          onClick={(e) => {
            if (e.detail > 1) return;
            if (throwError(validateQuestions(questions, { inSubject }))) return; // IF ERRORS, stop here
            const warns = getQuestionWarnings(questions, { inSubject });
            warns.forEach((w) => toast.warn(w));
            handleData({ questions, purposes, objectives });
            next();
          }}
        />
      </div>
    </div>
  );
}
